import { Business } from 'models/entities/business';
import { MenuCategory, MenuCategoryCollection } from 'models/entities/menu-category';
import { OriginalMenuCategory, OriginalMenuCategoryCollection } from 'models/entities/original-menu-category';
import { RelationshipCollection } from 'models/entities/relationship';

type Data = {
  target: Business;
  originalCategories: OriginalMenuCategoryCollection;
};

class Model {

  readonly target: Business;
  readonly categories: MenuCategoryCollection;
  readonly originalCategories: OriginalMenuCategoryCollection;
  readonly originalCategoryList: OriginalMenuCategory[];
  readonly relationship: Set<string>;
  readonly result: Business;

  constructor(data: Data) {
    this.target = data.target;
    this.categories = this.target.menuCategories;
    this.originalCategories = data.originalCategories;
    this.originalCategoryList = this.getOriginalCategoryList(this.originalCategories, this.categories);
    this.relationship = new Set();
    this.result = this.target;
  }

  async readAllCategories(): Promise<this> {
    const categories = await this.categories.readAll();
    const originalCategoryList = this.getOriginalCategoryList(this.originalCategories, categories);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, categories, originalCategoryList });
  }

  async readAllOriginalCategories(): Promise<this> {
    const originalCategories = await this.originalCategories.readAll();
    const originalCategoryList = this.getOriginalCategoryList(originalCategories, this.categories);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, originalCategories, originalCategoryList });
  }

  async readRelationshipCollections(): Promise<this> {
    const collections = await Promise.all(this.categories.documents.map(it => RelationshipCollection.readByMenuCategoryId(it.id!, 1)));
    const relationship = new Set(this.categories.documents.map((it, i) => collections[i].documents.length ? it.id : ''));
    relationship.delete('');
    return Object.assign(Object.create(this.constructor.prototype), { ...this, relationship });
  }

  build(originalCategory: OriginalMenuCategory): MenuCategory {
    const businessId = this.target.id!;
    const original = originalCategory;
    return MenuCategory.buildNext(this.categories.documents, { businessId, original });
  }

  buildBetween(prev: MenuCategory | undefined, next: MenuCategory | undefined, originalCategory: OriginalMenuCategory): MenuCategory {
    const businessId = this.target.id!;
    const original = originalCategory;
    return MenuCategory.buildBetween(prev, next, { businessId, original })
  }

  add(category: MenuCategory, done: boolean = false): this {
    return this.apply(this.categories.add(category), done);
  }

  remove(category: MenuCategory, done: boolean = false): this {
    return this.apply(this.categories.remove(category), done);
  }

  merge(category: MenuCategory, done: boolean = false): this {
    if (!category.id) return this.add(category);
    return this.apply(this.categories.replace(category), done);
  }

  private apply(categories: MenuCategoryCollection, done: boolean = false): this {
    const originalCategoryList = this.getOriginalCategoryList(this.originalCategories, categories);
    const result = done ? this.result.edit({ menuCategories: categories }) : this.result;
    return Object.assign(Object.create(this.constructor.prototype), { ...this, categories, originalCategoryList, result });
  }

  private getOriginalCategoryList(originalCategories: OriginalMenuCategoryCollection, categories: MenuCategoryCollection): OriginalMenuCategory[] {
    const ids = categories.documents.map(it => it.original.id);
    return originalCategories.documents.filter(it => !ids.includes(it.id));
  }

}

export { Model };